import React, { useState } from "react";
import { COLORS } from "../config/themes";
import { InputField, SelectField } from "../components";
import { supabase } from "../supabaseClient";

const SchoolSetup = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    schoolName: "",
    schoolType: "Early Learning Center",
    address: "",
    phone: "",
    email: user?.email || "",
    principal: user?.name || "",
    principalTitle: "Director",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
    hemisphere: "northern"
  });

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleFinish = async () => {
    if (!form.schoolName.trim()) { setError("Please enter a school name"); return; }
    setLoading(true); setError("");
    try {
      // 1. Create the school
      const { data: school, error: schoolErr } = await supabase.from("schools").insert({
        name: form.schoolName.trim(),
        school_type: form.schoolType,
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        principal: form.principal.trim(),
        principal_title: form.principalTitle,
        timezone: form.timezone,
        hemisphere: form.hemisphere
      }).select().single();

      if (schoolErr) { setError("Failed to create school: " + schoolErr.message); setLoading(false); return; }

      // 2. Create or update the user's profile with school_id
      const { error: profileErr } = await supabase.from("profiles").upsert({
        id: user.id,
        email: user.email,
        name: user.name,
        role: "admin",
        school_id: school.id,
        status: "active"
      });

      if (profileErr) { setError("Failed to create profile: " + profileErr.message); setLoading(false); return; }

      // 3. Send welcome email (non-blocking — don't fail setup if this fails)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await supabase.functions.invoke("send-email", {
            body: {
              type: "welcome",
              payload: {
                name: user.name || user.email,
                email: user.email,
                schoolName: form.schoolName.trim(),
              }
            }
          });
        }
      } catch (emailErr) {
        console.warn("Welcome email failed (non-fatal):", emailErr);
      }

      // 4. Call onComplete with school and profile data
      onComplete({
        school,
        profile: { id: user.id, name: user.name, email: user.email, role: "admin", school_id: school.id }
      });
    } catch (e) {
      setError("An unexpected error occurred: " + e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ fontFamily: "'Nunito', 'DM Sans', system-ui, sans-serif", background: "linear-gradient(135deg, #F4F7FB 0%, #DBEAFE 50%, #E8EEF4 100%)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.text }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Nunito:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <div style={{ background: COLORS.card, borderRadius: 28, padding: 40, width: 520, maxWidth: "92vw", boxShadow: "0 24px 80px rgba(0,0,0,0.08)", border: `1px solid ${COLORS.border}` }}>
        
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.accent})`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", color: "#fff", fontSize: 30 }}>🏫</div>
          <h2 style={{ margin: 0, fontSize: 24, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}>Set Up Your School</h2>
          <p style={{ margin: "6px 0 0", color: COLORS.textMuted, fontSize: 13 }}>
            {step === 1 ? "Let's get the basics down" : "Almost done — a few more details"}
          </p>
        </div>

        {/* Progress */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          {[1, 2].map(s => (
            <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: s <= step ? COLORS.primary : COLORS.borderLight, transition: "background 0.3s" }} />
          ))}
        </div>

        {error && <div style={{ padding: "10px 14px", borderRadius: 10, background: COLORS.dangerPale, color: COLORS.danger, fontSize: 12, fontWeight: 600, marginBottom: 16, textAlign: "center" }}>{error}</div>}

        {/* Step 1: School basics */}
        {step === 1 && (<div>
          <InputField label="School Name" value={form.schoolName} onChange={e => update("schoolName", e.target.value)} placeholder="e.g. Sunshine Academy" />
          <InputField label="Address" value={form.address} onChange={e => update("address", e.target.value)} placeholder="123 Learning Lane, City, State ZIP" />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={() => { if (!form.schoolName.trim()) { setError("Please enter a school name"); return; } setError(""); setStep(2); }} style={{ padding: "12px 32px", borderRadius: 12, border: "none", background: COLORS.primary, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Next</button>
          </div>
        </div>)}

        {/* Step 2: Contact & preferences */}
        {step === 2 && (<div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <InputField label="Phone" value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="(555) 123-4567" />
            <InputField label="School Email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="admin@school.edu" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <InputField label="Director / Principal" value={form.principal} onChange={e => update("principal", e.target.value)} placeholder="Your name" />
            <SelectField label="Title" value={form.principalTitle} onChange={e => update("principalTitle", e.target.value)} options={[
              { value: "Director", label: "Director" },
              { value: "Principal", label: "Principal" },
              { value: "Head of School", label: "Head of School" },
              { value: "Owner", label: "Owner" }
            ]} />
          </div>
          <SelectField label="Timezone" value={form.timezone} onChange={e => update("timezone", e.target.value)} options={[
            { value: "America/New_York", label: "Eastern Time (ET)" },
            { value: "America/Chicago", label: "Central Time (CT)" },
            { value: "America/Denver", label: "Mountain Time (MT)" },
            { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
            { value: "America/Anchorage", label: "Alaska Time (AKT)" },
            { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
            { value: "Europe/London", label: "London (GMT/BST)" },
            { value: "Europe/Paris", label: "Central Europe (CET)" },
            { value: "Asia/Tokyo", label: "Japan (JST)" },
            { value: "Australia/Sydney", label: "Sydney (AEST)" },
            { value: "Pacific/Auckland", label: "New Zealand (NZST)" }
          ]} />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <button onClick={() => setStep(1)} style={{ padding: "12px 24px", borderRadius: 12, border: `1px solid ${COLORS.border}`, background: COLORS.card, color: COLORS.textMuted, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Back</button>
            <button onClick={handleFinish} disabled={loading} style={{ padding: "12px 32px", borderRadius: 12, border: "none", background: COLORS.primary, color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
              {loading ? "Creating..." : "Launch My School"}
            </button>
          </div>
        </div>)}

        {/* Footer hint */}
        <div style={{ textAlign: "center", marginTop: 20, fontSize: 11, color: COLORS.textMuted }}>
          You can update all of this later in Settings
        </div>
      </div>
    </div>
  );
};

export default SchoolSetup;
